import AreaIndicator from '../common/AreaIndicator.jsx';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import ErrorIcon from '@mui/icons-material/Error';
import ErrorMessage from '@components/common/ErrorMessage';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FeaturesGrid from './FeaturesGrid.jsx';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import MinimumCostAlert from './MinimumCostAlert.jsx';
import QuotaDisplay from './QuotaDisplay.jsx';
import React, {useEffect, useMemo, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {QUOTA_MANAGER_MODES} from '@app/constants/quotaManager';
import {array, bool, func, object, string} from 'prop-types';
import {getFeatureReference} from '@apps-common/geocorn-sdk/api/util';
import {getMinimumSQMCost} from './util.js';
import {notify} from '@actions/notifications';
import {trackEvent} from '@app/analytics';
import {useDispatch} from 'react-redux';
import {useGetEstimates} from '@app/api/quotaEstimates';
import {useMakeQuotaReservations} from '@api/quotaReservations';
import {useTranslation} from 'react-i18next';

const ProductCard = ({
  product,
  features,
  loading,
  selectedFeatureRefs,
  setSelectedFeatureRefs,
  onZoomToFeature,
  onReserve,
  mode,
  collectionId,
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const [featuresExpanded, setFeaturesExpanded] = useState(false);
  const [featuresToReserve, setFeaturesToReserve] = useState([]);

  const featureRefs = useMemo(
    () => features?.map(feature => getFeatureReference(feature)),
    [features]
  );

  const {
    data: estimatesById,
    isFetching: fetchingEstimates,
    isLoading: loadingEstimates,
    isError: estimatesError,
    refetch: refetchEstimates,
  } = useGetEstimates(
    {
      aoi_refs: featureRefs,
      product_access_id: product.id,
    },
    {
      skip: !featureRefs?.length,
    }
  );

  const selectionsCost = useMemo(() => {
    let cost = 0;
    selectedFeatureRefs.forEach(featureRef => {
      cost += estimatesById[featureRef]?.cost || 0;
    });

    return cost;
  }, [estimatesById, selectedFeatureRefs]);

  const remainingQuota = useMemo(
    () => product.quota_total - product.quota_used - selectionsCost,
    [product.quota_total, product.quota_used, selectionsCost]
  );

  const hasRemainingQuota = product.unlimited_quota || remainingQuota > 0;

  useEffect(() => {
    if (estimatesById) {
      setFeaturesToReserve(
        features.filter(f => !estimatesById[getFeatureReference(f)]?.reserved)
      );
    } else {
      setFeaturesToReserve([]);
    }
  }, [estimatesById, features]);

  const onRowClick = values => {
    setSelectedFeatureRefs(values);
  };

  const {mutate: makeReservations, isPending: isReserving} =
    useMakeQuotaReservations({
      onSuccess: data => {
        const hasSuccess = data.filter(d => !d.error).length > 0;
        const hasErrors = data.filter(d => !!d.error).length > 0;

        if (hasSuccess) {
          onReserve(hasErrors);
          trackEvent('Product Card', {
            action: 'reserved quota successfully',
            product,
            featuresLength: features.length,
          });
          dispatch(
            notify({
              message: `Your request is being processed ${selectedFeatureRefs.length >= 100 ? ', it may take a few minutes to complete' : ''}. Reservations will show up in the "Reserved" tab.`,
              persist: true,
            })
          );
        }

        if (hasErrors) {
          dispatch(
            notify({
              severity: 'error',
              message: t('productCard.reserveError', {
                condition: hasSuccess.length ? 'some of the ' : '',
              }),
            })
          );
        }
      },
    });

  const onReserveClick = async () => {
    makeReservations({
      productId: product.id,
      refs: selectedFeatureRefs,
      collectionId,
    });
  };

  useEffect(() => {
    const selectedRefs = featuresToReserve.map(f => getFeatureReference(f));

    setSelectedFeatureRefs(selectedRefs);
  }, [featuresToReserve, setSelectedFeatureRefs]);

  useEffect(() => {
    setFeaturesExpanded(false);
  }, [product]);

  if (!product) {
    return false;
  }

  if (estimatesError) {
    return (
      <ErrorMessage
        onRetry={refetchEstimates}
        statusCode={estimatesError?.response?.status}
        message={t('productCard.quotaEstimateError')}
      />
    );
  }

  return (
    <>
      <Card
        elevation={2}
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent>
          <Grid container rowSpacing={1} direction="column">
            <Grid container direction="row" xs={12} alignItems="center">
              <Grid xs={8}>
                <Typography variant="subtitle1" display="inline">
                  {product.title}
                  <Tooltip
                    title={`Access dates: ${t('common:date_ranges.toRange', {
                      startDate: product?.term_start_date,
                      endDate: product?.term_end_date,
                    })}`}
                    placement="top"
                  >
                    <CalendarIcon
                      color="action"
                      sx={{
                        marginTop: -0.5,
                        verticalAlign: 'middle',
                        marginLeft: 1,
                      }}
                    />
                  </Tooltip>
                </Typography>
              </Grid>
              <Grid xs={4} sx={{textAlign: 'right'}}>
                {mode === QUOTA_MANAGER_MODES.MANAGE && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={
                      loading ||
                      loadingEstimates ||
                      fetchingEstimates ||
                      selectedFeatureRefs.length === 0 ||
                      !hasRemainingQuota ||
                      isReserving ||
                      !!estimatesError
                    }
                    sx={{marginLeft: 'auto'}}
                    onClick={onReserveClick}
                  >
                    {isReserving ? 'Reserving...' : 'Reserve quota'}
                  </Button>
                )}
              </Grid>
            </Grid>
            <Grid container direction="row" xs={12}>
              <Grid xs={8}>
                <Typography variant="subtitle1">Quota available</Typography>
              </Grid>
              <Grid xs={4}>
                <Typography
                  variant="subtitle1"
                  color="primary"
                  textAlign="right"
                >
                  <QuotaDisplay
                    quota={product.quota_total - product.quota_used}
                    isUnlimited={product.unlimited_quota}
                  />
                </Typography>
              </Grid>
            </Grid>
            <Grid container direction="row" xs={12}>
              <Grid xs={8}>
                <Typography>Quota cost</Typography>
              </Grid>
              <Grid xs={4}>
                <Typography textAlign="right">
                  {loadingEstimates || fetchingEstimates || loading ? (
                    'Loading...'
                  ) : (
                    <AreaIndicator squareMeters={selectionsCost} />
                  )}
                </Typography>
              </Grid>
            </Grid>
            {!product.unlimited_quota && (
              <>
                <Grid container direction="row" xs={12} sx={{marginY: 0.5}}>
                  <Grid xs={9}></Grid>
                  <Grid xs={3}>
                    <Divider flexItem />
                  </Grid>
                </Grid>
                <Grid container direction="row" xs={12}>
                  <Grid xs={8}>
                    <Typography>Remaining quota</Typography>
                  </Grid>
                  <Grid xs={4}>
                    <Typography
                      justifyContent="flex-end"
                      display="flex"
                      alignItems="center"
                    >
                      {loading || loadingEstimates || fetchingEstimates ? (
                        'Loading...'
                      ) : (
                        <>
                          {!hasRemainingQuota && (
                            <Tooltip title={t('productCard.insufficientQuota')}>
                              <ErrorIcon
                                color="error"
                                sx={{marginRight: 0.5}}
                              />
                            </Tooltip>
                          )}
                          <AreaIndicator squareMeters={remainingQuota} />
                        </>
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
            <Grid container direction="row" xs={12} sx={{marginTop: 2}}>
              <Grid>
                <MinimumCostAlert product={product} />
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
        <Divider sx={{marginTop: 1}} />
        <CardActions sx={{width: '100%', justifyContent: 'space-between'}}>
          <Stack direction="row" alignItems="center" spacing={1} flexShrink={0}>
            <IconButton
              size="small"
              disabled={loading || loadingEstimates}
              onClick={() => setFeaturesExpanded(prevState => !prevState)}
              sx={{
                transform: !featuresExpanded
                  ? 'rotate(0deg)'
                  : 'rotate(180deg)',
                transition: theme =>
                  theme.transitions.create('transform', {
                    duration: theme.transitions.duration.shortest,
                  }),
              }}
            >
              {loading || loadingEstimates || fetchingEstimates ? (
                <CircularProgress size={24} />
              ) : (
                <ExpandMoreIcon />
              )}
            </IconButton>
            <Typography variant="subtitle1">
              Features{' '}
              {loading || loadingEstimates
                ? ''
                : `(${featuresToReserve.length})`}
            </Typography>
          </Stack>
          {featuresToReserve.length === 0 && !loading && !loadingEstimates && (
            <Alert severity="info" sx={{paddingX: 1}}>
              Product has been reserved for all features in this collection.
            </Alert>
          )}
        </CardActions>
        <Collapse
          in={featuresExpanded}
          timeout="auto"
          unmountOnExit
          sx={{
            '.MuiCollapse-wrapper': {
              // keeps data grid happy and not janky
              height: '100%',
              minHeight: 150,
            },
          }}
        >
          <Box height="100%" sx={{minHeight: 50, maxHeight: 500}}>
            <FeaturesGrid
              features={featuresToReserve}
              estimates={estimatesById}
              rowSelectionModel={selectedFeatureRefs}
              onRowSelectionModelChange={onRowClick}
              loading={loading}
              onZoomToClicked={onZoomToFeature}
              disableSelection={isReserving}
              minCost={getMinimumSQMCost(product)}
            />
          </Box>
        </Collapse>
      </Card>
    </>
  );
};

ProductCard.propTypes = {
  features: array,
  product: object,
  loading: bool,
  selectedFeatureRefs: array,
  setSelectedFeatureRefs: func,
  onZoomToFeature: func,
  mode: string,
  onReserve: func,
  collectionId: string,
};

export default ProductCard;
